import type { BilletTabsComponent } from "../../types";
import { RuntimeLink } from "../../runtime/RendererRuntime";
import { resolveLinkHref } from "../../utils/links";

/**
 * Quick denomination switcher for the banknote ("billet") detail pages.
 *
 * Eight tabs (50 FC … 20 000 FC); the page sets `activeIndex` to mark the
 * current denomination — a blue pill — while the others are quiet underlined
 * links to the sibling pages. Everything is authored manually on the widget
 * (no dynamic/D1 data); navigation is client-side via {@link RuntimeLink} so
 * switching between billets is instant.
 */
export function BilletTabsRenderer({ component }: { component: BilletTabsComponent }) {
  const { tabs = [], activeIndex = 0 } = component.content ?? {};
  if (!tabs.length) return null;

  return (
    <section className="mt-2">
      <div className="flex flex-nowrap items-center gap-x-5 gap-y-3 overflow-x-auto pb-1 sm:flex-wrap sm:overflow-visible">
        {tabs.map((tab, idx) => {
          const isActive = idx === activeIndex;
          if (isActive) {
            return (
              <span
                key={`${tab.label}-${idx}`}
                aria-current="page"
                className="shrink-0 rounded-md bg-[#00B862] px-4 py-1.5 text-sm xl:text-base font-semibold text-white shadow-sm"
              >
                {tab.label}
              </span>
            );
          }
          const href = resolveLinkHref(tab.href);
          if (!href) {
            return (
              <span key={`${tab.label}-${idx}`} className="shrink-0 border-b-2 border-slate-200 px-1 pb-1 text-sm xl:text-base font-medium text-slate-400">
                {tab.label}
              </span>
            );
          }
          return (
            <RuntimeLink
              key={`${tab.label}-${idx}`}
              href={href}
              className="shrink-0 border-b-2 border-slate-200 px-1 pb-1 text-sm xl:text-base font-medium text-slate-400 transition-colors hover:border-[#00B862] hover:text-[#00B862]"
            >
              {tab.label}
            </RuntimeLink>
          );
        })}
      </div>
    </section>
  );
}
